import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { conteoFisicoService, ConteoFisicoDetalleCreate } from '../../services/conteoFisicoService';
import { productoService } from '../../services/productoService';
import { varianteService } from '../../services/varianteService';
import AgregarDetalleForm from '../../components/ConteosFisicos/AgregarDetalleForm';

const ConteoFisicoDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAgregarDetalle, setShowAgregarDetalle] = useState(false);

  const { data, isLoading, error } = useQuery(
    ['conteo-fisico', id],
    () => conteoFisicoService.getById(Number(id!)),
    { enabled: !!id }
  );

  const iniciarMutation = useMutation(
    () => conteoFisicoService.iniciar(Number(id!)),
    {
      onSuccess: () => {
        toast.success('Conteo iniciado correctamente');
        queryClient.invalidateQueries(['conteo-fisico', id]);
        queryClient.invalidateQueries(['conteos-fisicos']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al iniciar conteo');
      }
    }
  );

  const finalizarMutation = useMutation(
    ({ aplicarAjustes }: { aplicarAjustes: boolean }) =>
      conteoFisicoService.finalizar(Number(id!), aplicarAjustes),
    {
      onSuccess: () => {
        toast.success('Conteo finalizado correctamente');
        queryClient.invalidateQueries(['conteo-fisico', id]);
        queryClient.invalidateQueries(['conteos-fisicos']);
        queryClient.invalidateQueries(['existencias']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al finalizar conteo');
      }
    }
  );

  const eliminarDetalleMutation = useMutation(
    (detalleId: number) => conteoFisicoService.eliminarDetalle(Number(id!), detalleId),
    {
      onSuccess: () => {
        toast.success('Detalle eliminado correctamente');
        queryClient.invalidateQueries(['conteo-fisico', id]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al eliminar detalle');
      }
    }
  );

  const handleIniciar = () => {
    iniciarMutation.mutate();
  };

  const handleFinalizar = () => {
    const aplicarAjustes = window.confirm(
      '¿Deseas aplicar los ajustes de inventario automáticamente?\n\n' +
      'Sí: Se ajustarán las existencias según las diferencias encontradas.\n' +
      'No: Solo se finalizará el conteo sin aplicar ajustes.'
    );
    finalizarMutation.mutate({ aplicarAjustes });
  };

  const handleEliminarDetalle = (detalleId: number) => {
    if (window.confirm('¿Estás seguro de eliminar este detalle?')) {
      eliminarDetalleMutation.mutate(detalleId);
    }
  };

  if (isLoading) return <div>Cargando conteo físico...</div>;
  if (error) return <div>Error al cargar conteo físico</div>;

  const conteo = data?.data.conteo;
  if (!conteo) return <div>Conteo no encontrado</div>;

  const detalles = conteo.detalles || [];
  const totalDiferencia = detalles.reduce((sum, d) => sum + (d.diferencia * d.costoUnitario), 0);

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-gray-100 text-gray-800';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800';
      case 'finalizado':
        return 'bg-green-100 text-green-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'Pendiente';
      case 'en_proceso':
        return 'En Proceso';
      case 'finalizado':
        return 'Finalizado';
      case 'cancelado':
        return 'Cancelado';
      default:
        return estado;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <button
            onClick={() => navigate('/conteos-fisicos')}
            className="text-primary-600 hover:text-primary-800 mb-2"
          >
            ← Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Conteo Físico #{conteo.id}</h1>
        </div>
        <div className="flex space-x-2">
          {conteo.estado === 'pendiente' && (
            <button
              onClick={handleIniciar}
              disabled={iniciarMutation.isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Iniciar Conteo
            </button>
          )}
          {conteo.estado === 'en_proceso' && (
            <>
              <button
                onClick={() => setShowAgregarDetalle(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                + Agregar Producto
              </button>
              <button
                onClick={handleFinalizar}
                disabled={finalizarMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {finalizarMutation.isLoading ? 'Finalizando...' : 'Finalizar Conteo'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Sucursal</div>
          <div className="text-lg font-semibold">{conteo.sucursal?.name || 'N/A'}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Estado</div>
          <span className={`px-2 py-1 text-sm rounded-full ${getEstadoColor(conteo.estado)}`}>
            {getEstadoLabel(conteo.estado)}
          </span>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Productos Contados</div>
          <div className="text-lg font-semibold">{detalles.length}</div>
        </div>
      </div>

      {conteo.notas && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="text-sm font-medium text-gray-700 mb-1">Notas</div>
          <div className="text-sm text-gray-600">{conteo.notas}</div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Productos Contados</h2>
        </div>
        {detalles.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No hay productos contados aún
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sistema
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Diferencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Diferencia
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {detalles.map((detalle) => (
                  <tr key={detalle.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {detalle.producto?.name || 'N/A'}
                      </div>
                      {detalle.variante && (
                        <div className="text-xs text-gray-500">
                          Variante: {detalle.variante.name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {detalle.cantidadSistema.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {detalle.cantidadContada.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${
                          detalle.diferencia > 0
                            ? 'text-green-600'
                            : detalle.diferencia < 0
                            ? 'text-red-600'
                            : 'text-gray-600'
                        }`}
                      >
                        {detalle.diferencia > 0 ? '+' : ''}
                        {detalle.diferencia.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${(detalle.diferencia * detalle.costoUnitario).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {conteo.estado === 'en_proceso' && (
                        <button
                          onClick={() => handleEliminarDetalle(detalle.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detalles.length > 0 && (
              <div className="px-6 py-4 border-t bg-gray-50">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Total Diferencia en Valor:</span>
                  <span className={`text-lg font-bold ${
                    totalDiferencia > 0 ? 'text-green-600' : totalDiferencia < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {totalDiferencia > 0 ? '+' : ''}${totalDiferencia.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showAgregarDetalle && (
        <AgregarDetalleForm
          conteoId={Number(id!)}
          onClose={() => setShowAgregarDetalle(false)}
        />
      )}
    </div>
  );
};

export default ConteoFisicoDetail;

